import React, { MouseEventHandler, useMemo } from 'react';

// Components
import { DropdownButtonMenuItem } from './DropdownButton';
import DropdownSplitButton from './DropdownSplitButton';
import { IconName } from '@capintel/component-lib/icon';
import { Button } from '@capintel/component-lib/button';

export type buttonAction = {
  condition: boolean;
  'data-testid'?: string;
  icon: IconName;
  onClick?: () => void;
  node: JSX.Element | string;
};

type validButtonAction = Omit<buttonAction, 'onClick'> & {
  onClick: () => void;
};

const BUTTON_VARIANT = 'primary';

/*
  Rendering based on the actions array passed in prop :
   - if only one action is available to the user, a simple primary button will be rendered
   - if more than one action are available, a dropdown split button will be rendered having all the available actions listed

   Current limitation : only for variant primary, but easy to adapt for more variants
 */
const ActionsButton: React.FC<{
  actions: buttonAction[];
  disabled?: boolean;
}> = ({ actions, disabled }: { actions: buttonAction[]; disabled?: boolean }) => {
  const displayedActions = useMemo(() => actions.filter((item): item is validButtonAction => item.condition && !!item.onClick), [actions]);

  const renderToggleButton = () => {
    if (displayedActions.length <= 1) {
      return null;
    }

    const mainAction = displayedActions[0];
    const dropdownActions = displayedActions
      .filter(action => action !== mainAction)
      .map(action => ({
        'data-testid': action['data-testid'],
        hidden: false,
        icon: typeof action.icon === 'string' ? action.icon.replace(/icon-/, '') : action.icon,
        node: action.node,
        onClick: action.onClick as MouseEventHandler<DropdownButtonMenuItem>,
      }));

    return (
      // TODO: button - when split button is available and replaced here,
      // make sure the icons work! they are currently broken because the icons
      // on the dropdownActions are not what this DropdownSplitButton expects.
      <DropdownSplitButton
        align="end"
        btnOnClick={mainAction.onClick}
        data-testid={mainAction['data-testid']}
        items={dropdownActions}
        variant={BUTTON_VARIANT}
        disabled={disabled}
      >
        {typeof mainAction.icon === 'string' ? <i className={mainAction.icon} /> : mainAction.icon}
        {mainAction.node}
      </DropdownSplitButton>
    );
  };

  const renderButton = () => {
    if (displayedActions.length !== 1) {
      return null;
    }
    const action = displayedActions[0];
    return (
      <Button variant="primary" icon={action.icon} onClick={action.onClick} data-testid={action['data-testid']} disabled={disabled}>
        {action.node}
      </Button>
    );
  };

  if (!displayedActions || displayedActions.length === 0) {
    return null;
  }

  return (
    <>
      {renderButton()}
      {renderToggleButton()}
    </>
  );
};

export default ActionsButton;
